import { tokens as defaultTokens, type ThemeTokens } from './tokens.js';

/**
 * Embed-safe, scoped CSS for the AI Helper conversational surface (Story 4.1,
 * Task 2). Every rule is namespaced under the `.ah-root` container with an
 * `ah-` class prefix so nothing leaks to (or is inherited from) the host page —
 * the widget is iframe-isolated, but we still fully self-scope and reset the
 * base box model rather than relying on host globals.
 *
 * The streaming reveal / typing indicator motion is disabled under
 * `prefers-reduced-motion: reduce` (opacity/instant only) per
 * front-end-spec.md#Animation & Micro-interactions.
 */
export function widgetCss(t: ThemeTokens = defaultTokens): string {
  return `
.ah-root, .ah-root *, .ah-root *::before, .ah-root *::after { box-sizing: border-box; }
.ah-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  position: relative;
  background: ${t.panelBg};
  color: ${t.darkText};
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  line-height: 1.5;
}
.ah-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ah-welcome { display: flex; flex-direction: column; gap: 12px; }
.ah-greeting { font-size: 20px; font-weight: 700; color: ${t.darkText}; margin: 0; }
.ah-intro { color: ${t.neutralGray}; margin: 0; }
/* "Powered by SkillsPass" brand lockup (embedded PNG). Fixed display width;
   height scales with the artwork's aspect ratio. */
.ah-poweredby { width: 198px; height: auto; display: block; }

/* "New message ↓" jump pill — floats just above the docked input when a message
   arrives while the user is scrolled up (Feature: chat autoscroll). Absolute
   within .ah-root so it overlays the thread rather than displacing it. */
.ah-jump {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 92px;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 0;
  border-radius: 999px;
  background: ${t.skillsPassBlue};
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);
}
.ah-jump:hover { filter: brightness(1.05); }
.ah-jump:focus-visible { outline: 2px solid ${t.skillsPassBlue}; outline-offset: 2px; }

.ah-message { display: flex; flex-direction: column; max-width: 100%; }
.ah-message--user { align-items: flex-end; }
.ah-message--assistant { align-items: flex-start; }
.ah-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 14px;
  overflow-wrap: anywhere;
}
.ah-message--user .ah-bubble { background: ${t.userBubbleBg}; color: ${t.darkText}; }
/* The agent answer gets its OWN bubble — a light/white surface with a subtle
   border, DISTINCT from the user's gray right-aligned bubble. It wraps the
   answer text, the citations/sources, and the per-answer thumbs footer. */
.ah-message--assistant .ah-bubble {
  background: ${t.panelBg}; color: ${t.darkText};
  border: 1px solid ${t.userBubbleBg};
  border-top-left-radius: 4px;
}
.ah-avatar {
  display: inline-flex; align-items: center; justify-content: center;
  color: ${t.gradientEnd}; font-size: 16px; margin-bottom: 2px;
}
.ah-answer p:first-child { margin-top: 0; }
.ah-answer p:last-child { margin-bottom: 0; }
.ah-answer a { color: ${t.skillsPassBlue}; }
.ah-answer sup { color: ${t.skillsPassBlue}; font-weight: 700; }
.ah-sources { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 6px; align-items: stretch; }

/* Citation pill (text source) — subtle-bordered white chip, blue number badge
   pairing the SkillsPass-blue inline superscript (Story 4.2). */
.ah-pill {
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 28px; max-width: 100%; padding: 3px 10px 3px 3px;
  background: ${t.panelBg}; color: ${t.darkText};
  border: 1px solid ${t.userBubbleBg}; border-radius: 999px;
  text-decoration: none; font-size: 12px; line-height: 1.3;
  transition: border-color 120ms ease-out, transform 120ms ease-out;
}
.ah-pill:hover { border-color: ${t.skillsPassBlue}; transform: translateY(-1px); }
.ah-pill--static { cursor: default; }
.ah-pill__num {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  background: ${t.skillsPassBlue}; color: #fff; font-size: 11px; font-weight: 700;
}
.ah-pill__title {
  min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  max-width: 220px;
}
/* Interaction 1: make the navigable pill an obvious link — the title is
   underlined and tints to the link blue on hover/focus. The static (non-link)
   chip is not underlined. */
a.ah-pill { cursor: pointer; }
a.ah-pill .ah-pill__title { text-decoration: underline; text-underline-offset: 2px; }
a.ah-pill:hover .ah-pill__title, a.ah-pill:focus-visible .ah-pill__title { color: ${t.skillsPassBlue}; }

/* Video card (video source) — a media card with a 16:9 thumbnail that PLAYS
   (posts OPEN_VIDEO to the host lightbox) + a caption row that LINKS to the
   article (Interactions 1 & 2). The card itself is a plain container; the
   thumbnail and caption are independently interactive. */
.ah-video {
  display: flex; flex-direction: column; width: 220px; max-width: 100%;
  min-height: 44px; overflow: hidden;
  background: ${t.panelBg}; color: ${t.darkText};
  border: 1px solid ${t.userBubbleBg}; border-radius: 12px;
  transition: border-color 120ms ease-out;
}
.ah-video:hover { border-color: ${t.skillsPassBlue}; }
.ah-video--static { cursor: default; }
.ah-video__thumb {
  position: relative; display: block; width: 100%; aspect-ratio: 16 / 9;
  background: ${t.userBubbleBg}; overflow: hidden;
  padding: 0; border: none; margin: 0; color: inherit; text-decoration: none;
}
.ah-video__thumb--play { cursor: pointer; }
.ah-video__thumb--static { cursor: default; }
.ah-video__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ah-video__play {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: 50%;
  background: rgba(26, 35, 50, 0.72); color: #fff; font-size: 15px;
  transition: background-color 120ms ease-out, transform 120ms ease-out;
}
.ah-video__thumb--play:hover .ah-video__play { background: ${t.gradientEnd}; transform: translate(-50%, -50%) scale(1.06); }
.ah-video__meta {
  display: flex; align-items: center; gap: 6px; padding: 8px 10px;
  color: ${t.darkText}; text-decoration: none;
}
.ah-video__num {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 50%;
  background: ${t.skillsPassBlue}; color: #fff; font-size: 11px; font-weight: 700;
}
.ah-video__title {
  min-width: 0; flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; font-size: 12px;
}
/* Interaction 1: the caption is an obvious article link. */
a.ah-video__meta .ah-video__title { text-decoration: underline; text-underline-offset: 2px; }
a.ah-video__meta:hover .ah-video__title, a.ah-video__meta:focus-visible .ah-video__title { color: ${t.skillsPassBlue}; }
.ah-video__time { flex: 0 0 auto; color: ${t.neutralGray}; font-size: 11px; }

.ah-topics { display: flex; flex-direction: column; gap: 8px; }
.ah-topics__label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: ${t.neutralGray}; margin: 8px 0 2px;
}
/* Mockup suggested-action rows: full-width bordered rows, bold label left,
   ">" chevron right. */
.ah-topic {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; width: 100%; min-height: 52px; padding: 14px 16px;
  background: ${t.panelBg}; color: ${t.darkText};
  border: 1px solid ${t.userBubbleBg}; border-radius: 12px;
  font: inherit; font-weight: 700; text-align: left; cursor: pointer;
  transition: border-color 120ms ease-out, background-color 120ms ease-out, transform 120ms ease-out;
}
.ah-topic:hover { border-color: ${t.gradientEnd}; background: ${t.userBubbleBg}; transform: translateY(-1px); }
.ah-topic__chevron { flex: 0 0 auto; color: ${t.gradientEnd}; font-weight: 700; }

.ah-inputbar {
  flex: 0 0 auto; display: flex; flex-direction: column; gap: 6px;
  padding: 12px 16px; border-top: 1px solid ${t.userBubbleBg};
}
.ah-inputrow { display: flex; align-items: flex-end; gap: 8px; }
/* Rounded pill text input + circular send (mockup). */
.ah-input {
  flex: 1 1 auto; resize: none; min-height: 44px; max-height: 120px;
  padding: 11px 16px; border: 1px solid ${t.userBubbleBg}; border-radius: 999px;
  font: inherit; color: ${t.darkText}; background: ${t.userBubbleBg};
  transition: border-color 120ms ease-out, background-color 120ms ease-out;
}
.ah-input:focus { outline: none; border-color: ${t.skillsPassBlue}; background: ${t.panelBg}; }
.ah-input:disabled { opacity: 0.6; }
/* Neutral-gray by default; warms to the brand accent on hover/active. */
.ah-send {
  flex: 0 0 auto; width: 44px; height: 44px; border-radius: 50%; border: none;
  background: ${t.neutralGray}; color: #fff; font-size: 18px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background-color 120ms ease-out, transform 120ms ease-out;
}
.ah-send:not(:disabled):hover, .ah-send:not(:disabled):active {
  background: ${t.gradientEnd}; transform: translateY(-1px);
}
.ah-send:disabled { opacity: 0.5; cursor: not-allowed; }
.ah-disclaimer { font-size: 11px; color: ${t.neutralGray}; margin: 0; }
.ah-hint { font-size: 12px; color: ${t.gradientEnd}; margin: 0; }
.ah-language-note {
  font-size: 12px; line-height: 1.4; color: ${t.neutralGray}; margin: 0 0 8px;
  padding: 6px 8px; border-left: 2px solid ${t.neutralGray}; background: transparent;
}

.ah-typing { display: inline-flex; gap: 4px; padding: 8px 0; }
.ah-typing span {
  width: 6px; height: 6px; border-radius: 50%; background: ${t.neutralGray};
  animation: ah-blink 1.2s infinite ease-in-out both;
}
.ah-typing span:nth-child(2) { animation-delay: 0.2s; }
.ah-typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes ah-blink { 0%, 80%, 100% { opacity: 0.3; } 40% { opacity: 1; } }

.ah-error {
  display: flex; flex-direction: column; gap: 6px; padding: 10px 12px;
  border: 1px solid ${t.gradientEnd}; border-radius: 10px; color: ${t.gradientEnd};
}
.ah-retry {
  align-self: flex-start; min-height: 44px; padding: 8px 14px;
  border: 1px solid ${t.gradientEnd}; border-radius: 10px;
  background: ${t.panelBg}; color: ${t.gradientEnd}; font: inherit; cursor: pointer;
}

/* Per-answer thumbs footer (Story 4.3, redesigned). A footer row INSIDE the
   assistant bubble with a subtle top divider, so the thumbs read as "was THIS
   answer helpful" — tied to the single response, not the session. Inline BLACK
   SVG thumb icons (no emoji): subtle by default (~20% opacity), full black on
   hover/focus, and full black + filled/solid once selected. ≥ 44×44px targets. */
.ah-feedback {
  display: flex; align-items: center; gap: 2px;
  margin-top: 8px; padding-top: 6px;
  border-top: 1px solid ${t.userBubbleBg};
}
.ah-feedback__btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; padding: 0;
  background: transparent; border: none; border-radius: 10px;
  line-height: 1; cursor: pointer; color: #000;
  transition: background-color 120ms ease-out;
}
.ah-feedback__icon {
  display: block; opacity: 0.2;
  transition: opacity 120ms ease-out;
}
/* Hover/focus lifts the subtle icon to full black (only while interactive). */
.ah-feedback__btn:not(:disabled):hover { background: ${t.userBubbleBg}; }
.ah-feedback__btn:not(:disabled):hover .ah-feedback__icon,
.ah-feedback__btn:focus-visible .ah-feedback__icon { opacity: 1; }
/* Selected choice stays "on": full black + filled/solid (fill set on the SVG). */
.ah-feedback__btn--selected .ah-feedback__icon { opacity: 1; }
.ah-feedback__btn--submitted, .ah-feedback__btn:disabled { cursor: default; }
.ah-feedback__thanks { font-size: 12px; color: ${t.neutralGray}; margin-left: 4px; }

/* End-of-conversation resolution prompt — a compact card at the thread foot. */
.ah-resolution {
  display: flex; flex-direction: column; gap: 8px; margin-top: 8px;
  padding: 12px 14px; border: 1px solid ${t.userBubbleBg}; border-radius: 12px;
  background: ${t.panelBg};
}
.ah-resolution__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.ah-resolution__q { font-weight: 700; color: ${t.darkText}; }
.ah-resolution__dismiss {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; padding: 0; margin: -8px -8px -8px 0;
  background: transparent; border: none; border-radius: 10px;
  color: ${t.neutralGray}; font-size: 14px; cursor: pointer;
}
.ah-resolution__dismiss:hover { background: ${t.userBubbleBg}; }
.ah-resolution__actions { display: flex; gap: 8px; }
.ah-resolution__btn {
  min-height: 44px; padding: 8px 18px;
  background: ${t.panelBg}; color: ${t.darkText};
  border: 1px solid ${t.neutralGray}; border-radius: 10px;
  font: inherit; cursor: pointer;
  transition: background-color 120ms ease-out;
}
.ah-resolution__btn:hover { background: ${t.userBubbleBg}; }

/* "Open a support ticket" CTA (IDK/escalation) — prominent warm-gradient button.
   gradientStartOnText is the darkened stop so white text meets AA contrast. */
.ah-ticket-cta {
  display: inline-flex; align-items: center; justify-content: center;
  min-height: 44px; margin-top: 10px; padding: 10px 18px;
  background: linear-gradient(90deg, ${t.gradientStartOnText}, ${t.gradientEnd});
  color: #fff; border-radius: 12px; font-weight: 700; text-decoration: none;
  transition: opacity 120ms ease-out;
}
.ah-ticket-cta:hover { opacity: 0.92; }

/* Docked-panel chrome (Story 4.4). The panel wraps the ChatView (.ah-root) as
   an accessible dialog: gradient header (✦ title + close ✕) + a body that hosts
   the conversational surface. Prefixed ah-panel(star) and self-scoped so nothing
   bleeds to the host; the header uses gradientStartOnText→gradientEnd where the
   white title/✕ sit, so white-on-fill meets WCAG AA. */
/* Widget document root (inside the iframe, under #root which is height:100%).
   It MUST carry the full height so the fixed-height panel chain resolves:
   #root(100%) → .ah-widget-root(100%) → .ah-panel(100%) → .ah-panel__body(flex 1)
   → .ah-root(100%) → .ah-messages(flex 1, overflow-y:auto). Without this the
   panel would size to content and the conversation could not scroll internally
   (FIX B). min-height:0 lets the flexed message list actually shrink+scroll. */
.ah-widget-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.ah-widget-root > .ah-panel { flex: 1 1 auto; min-height: 0; }

.ah-panel, .ah-panel *, .ah-panel *::before, .ah-panel *::after { box-sizing: border-box; }
.ah-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: ${t.panelBg};
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
.ah-panel__header {
  flex: 0 0 auto;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(90deg, ${t.gradientStartOnText}, ${t.gradientEnd});
  color: #fff;
}
.ah-panel__title {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 16px; font-weight: 700; color: #fff;
}
.ah-panel__spark { color: #fff; font-size: 18px; line-height: 1; }
/* Right-hand cluster of white icon controls: new-chat, history, close. */
.ah-panel__actions { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 2px; margin-right: -6px; }
.ah-panel__icon {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; padding: 0;
  background: transparent; border: none; border-radius: 10px;
  color: #fff; font-size: 16px; line-height: 1; cursor: pointer;
  transition: background-color 120ms ease-out;
}
.ah-panel__icon:hover { background: rgba(255, 255, 255, 0.18); }
.ah-panel__icon[aria-disabled="true"] { opacity: 0.55; cursor: default; }
.ah-panel__icon[aria-disabled="true"]:hover { background: transparent; }
.ah-panel__icon svg { display: block; }
.ah-panel__body { flex: 1 1 auto; min-height: 0; display: flex; }
.ah-panel__body > .ah-root { flex: 1 1 auto; }
/* The close button lives in the panel chrome (outside .ah-root), so it needs
   its own visible focus ring. */
/* The dialog container gets programmatic focus on open (tabIndex=-1) so keyboard
   users land inside — it must NEVER render its own focus outline (that was the
   "white box" around the ✕). Only interactive DESCENDANTS ring, and only on
   keyboard nav (:focus-visible). */
.ah-panel:focus { outline: none; }
.ah-panel :focus-visible { outline: 2px solid #fff; outline-offset: 2px; }

/* Visible focus ring on every interactive element (a11y, Task 8). */
.ah-root :focus-visible {
  outline: 2px solid ${t.skillsPassBlue};
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .ah-typing span { animation: none; opacity: 0.6; }
  /* No hover/focus lift under reduced motion — instant/opacity only. */
  .ah-pill, .ah-video, .ah-topic, .ah-send, .ah-video__play { transition: none; }
  .ah-pill:hover, .ah-topic:hover, .ah-send:not(:disabled):hover { transform: none; }
  .ah-video__thumb--play:hover .ah-video__play { transform: translate(-50%, -50%); }
  .ah-feedback__btn, .ah-feedback__icon, .ah-resolution__btn, .ah-ticket-cta { transition: none; }
}
`;
}

/**
 * Self-contained styles for the HOST-document launcher (Story 4.4, Task 6). The
 * "Ask" button + the docked iframe wrapper live in the HOST page — OUTSIDE the
 * widget's `.ah-root` / the iframe — so they carry their OWN tightly-scoped,
 * `bln-ask-`-prefixed rules that cannot bleed into or inherit from the host.
 *
 * The gradient pill uses `gradientStartOnText`→`gradientEnd` (the darkened stop)
 * so the white "✦ Ask" text meets WCAG AA. The panel slides in from the docked
 * edge (~200–250ms), disabled under `prefers-reduced-motion`. On mobile
 * (≤767px) the panel is full-screen; on wider viewports it is right/left-docked
 * at ~380px full-height. The configured `zIndex` is applied inline.
 */
export function launcherCss(t: ThemeTokens = defaultTokens): string {
  return `
.bln-ask-btn, .bln-ask-btn *, .bln-ask-btn *::before, .bln-ask-btn *::after { box-sizing: border-box; }
.bln-ask-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  min-height: 44px; min-width: 44px; padding: 10px 18px;
  background: linear-gradient(90deg, ${t.gradientStartOnText}, ${t.gradientEnd});
  color: #fff; border: none; border-radius: 999px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 14px; font-weight: 700; line-height: 1; cursor: pointer;
  box-shadow: 0 6px 20px rgba(192, 32, 43, 0.24);
  transition: opacity 120ms ease-out, transform 120ms ease-out;
}
.bln-ask-btn:hover { opacity: 0.94; transform: translateY(-1px); }
.bln-ask-btn--compact { padding: 10px; width: 44px; }
.bln-ask-btn__spark { font-size: 16px; line-height: 1; }
.bln-ask-btn:focus { outline: none; }
.bln-ask-btn:focus-visible { outline: 3px solid ${t.skillsPassBlue}; outline-offset: 2px; }

/* A floating chat-style panel anchored at the BOTTOM edge (near the header "Ask"
   button), NOT a full-height top-to-bottom drawer: bounded height with a small
   offset from the corner, rounded, elevated. The entry animation slides UP from
   the docked corner (translateY), which reads well from the bottom. */
.bln-ask-overlay {
  position: fixed; bottom: 20px;
  width: 392px; max-width: calc(100vw - 20px);
  height: min(660px, calc(100dvh - 40px));
  max-height: calc(100dvh - 40px);
  background: transparent;
  opacity: 0; visibility: hidden; pointer-events: none;
  transition: transform 220ms ease-out, opacity 220ms ease-out, visibility 220ms ease-out;
}
.bln-ask-overlay--right { right: 20px; transform: translateY(24px); }
.bln-ask-overlay--left { left: 20px; transform: translateY(24px); }
.bln-ask-overlay--open { opacity: 1; visibility: visible; pointer-events: auto; transform: translateY(0); }
.bln-ask-iframe {
  display: block; width: 100%; height: 100%; border: none;
  border-radius: 16px;
  background: ${t.panelBg}; box-shadow: 0 24px 64px rgba(15, 39, 64, 0.24);
}

/* Host-page video lightbox (Interaction 2). Rendered by the launcher OUTSIDE the
   widget iframe so it can cover the whole host viewport. Fully bln-ask--scoped
   with its own box-model reset so it cannot inherit/bleed on the host. Embeds
   ONLY our known providers (built launcher-side from provider + videoId). */
.bln-ask-lightbox, .bln-ask-lightbox *, .bln-ask-lightbox *::before, .bln-ask-lightbox *::after { box-sizing: border-box; }
.bln-ask-lightbox {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  background: rgba(9, 14, 22, 0.82);
}
.bln-ask-lightbox__stage {
  position: relative; width: 100%; max-width: min(1100px, calc(100vw - 48px));
}
.bln-ask-lightbox__frame {
  position: relative; width: 100%; aspect-ratio: 16 / 9;
  max-height: calc(100dvh - 96px);
  background: #000; border-radius: 12px; overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55);
}
.bln-ask-lightbox__player { display: block; width: 100%; height: 100%; border: none; }
.bln-ask-lightbox__close {
  position: absolute; top: -48px; right: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; padding: 0;
  background: rgba(255, 255, 255, 0.14); border: none; border-radius: 10px;
  color: #fff; font-size: 18px; line-height: 1; cursor: pointer;
  transition: background-color 120ms ease-out;
}
.bln-ask-lightbox__close:hover { background: rgba(255, 255, 255, 0.28); }
.bln-ask-lightbox__close:focus { outline: none; }
.bln-ask-lightbox__close:focus-visible { outline: 3px solid ${t.skillsPassBlue}; outline-offset: 2px; }
@media (max-width: 767px) {
  .bln-ask-lightbox { padding: 12px; }
  .bln-ask-lightbox__close { top: -44px; }
}

/* Mobile: full-width, near-full-height sheet flush to the edges (no corner
   offset / rounding), preserving the pre-existing small-screen behavior. */
@media (max-width: 767px) {
  .bln-ask-overlay {
    width: 100%; max-width: 100vw;
    left: 0; right: 0; bottom: 0;
    height: 100dvh; max-height: 100dvh;
  }
  .bln-ask-iframe { border-radius: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .bln-ask-btn, .bln-ask-overlay { transition: none; }
  .bln-ask-btn:hover { transform: none; }
}
`;
}
